(() => {
  const root = document.documentElement;
  const storageKey = "naxosColorTheme";
  const colorSchemeQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
  const reducedMotionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
  let requestedTheme = getActiveTheme();
  let themeTransition = null;
  let backgroundTransitionTimer = 0;

  function readSavedTheme() {
    try {
      const theme = window.localStorage.getItem(storageKey);
      return theme === "dark" || theme === "light" ? theme : null;
    } catch (_error) {
      return null;
    }
  }

  function saveTheme(theme) {
    try {
      window.localStorage.setItem(storageKey, theme);
    } catch (_error) {
      // The selected theme remains active for this page when storage is blocked.
    }
  }

  function getActiveTheme() {
    return root.classList.contains("naxos-theme-dark") ? "dark" : "light";
  }

  function syncThemeColor(theme = getActiveTheme()) {
    const themeColor = document.head.querySelector('meta[name="theme-color"]');
    const desiredColor = theme === "dark" ? "#0b1320" : "#001c3d";

    if (themeColor?.getAttribute("content") !== desiredColor) {
      themeColor?.setAttribute("content", desiredColor);
    }
  }

  function syncToggle(button, theme) {
    const isDark = theme === "dark";
    const nextLabel = isDark
      ? button.dataset.labelLight || window.NaxosText("naxos_ui.enable_light")
      : button.dataset.labelDark || window.NaxosText("naxos_ui.enable_dark");

    button.setAttribute("aria-pressed", String(isDark));
    button.setAttribute("aria-label", nextLabel);
    button.setAttribute("title", nextLabel);
  }

  function syncToggles(theme) {
    document.querySelectorAll("[data-naxos-theme-toggle]").forEach((button) => {
      syncToggle(button, theme);
    });
  }

  function announce(theme) {
    let status = document.querySelector("[data-naxos-theme-status]");

    if (!status) {
      status = document.createElement("p");
      status.className = "naxos-theme-status";
      status.dataset.naxosThemeStatus = "";
      status.setAttribute("role", "status");
      status.setAttribute("aria-live", "polite");
      status.setAttribute("aria-atomic", "true");
      document.body.append(status);
    }

    status.textContent = "";
    window.requestAnimationFrame(() => {
      status.textContent = theme === "dark" ? window.NaxosText("naxos_ui.dark_enabled") : window.NaxosText("naxos_ui.light_enabled");
    });
  }

  function applyTheme(theme, { persist = false, shouldAnnounce = false } = {}) {
    const normalizedTheme = theme === "dark" ? "dark" : "light";
    const isDark = normalizedTheme === "dark";

    root.classList.toggle("naxos-theme-dark", isDark);
    root.dataset.naxosTheme = normalizedTheme;
    root.style.colorScheme = normalizedTheme;

    syncThemeColor(normalizedTheme);

    if (persist) saveTheme(normalizedTheme);
    syncToggles(normalizedTheme);
    if (shouldAnnounce) announce(normalizedTheme);

    document.dispatchEvent(
      new CustomEvent("naxos:theme-change", {
        detail: { theme: normalizedTheme },
      })
    );
  }

  function changeTheme(theme, options) {
    requestedTheme = theme;
    themeTransition?.skipTransition();
    window.clearTimeout(backgroundTransitionTimer);
    root.classList.remove("naxos-theme-background-transition");

    const update = () => {
      // A newer click wins if a previous snapshot is still being prepared.
      if (theme === requestedTheme) applyTheme(theme, options);
    };
    const shouldAnimate = root.classList.contains("naxos-theme-ready") &&
      !reducedMotionQuery?.matches && !root.classList.contains("naxos-a11y-reduce-motion");

    if (!shouldAnimate) {
      update();
      return;
    }

    if (typeof document.startViewTransition !== "function") {
      root.classList.add("naxos-theme-background-transition");
      update();
      backgroundTransitionTimer = window.setTimeout(() => {
        root.classList.remove("naxos-theme-background-transition");
      }, 500);
      return;
    }

    // Crossfade the rendered backgrounds, including gradients that CSS cannot interpolate.
    root.classList.add("naxos-theme-transition");
    const transition = document.startViewTransition(update);
    themeTransition = transition;
    const cleanup = () => {
      if (themeTransition !== transition) return;
      themeTransition = null;
      root.classList.remove("naxos-theme-transition");
    };
    transition.ready.catch(() => {}); // A rapid toggle may skip the previous snapshot.
    transition.finished.then(cleanup, cleanup);
  }

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest?.("[data-naxos-theme-toggle]");
    if (!toggle) return;

    changeTheme(requestedTheme === "dark" ? "light" : "dark", {
      persist: true,
      shouldAnnounce: true,
    });
  });

  document.addEventListener("shopify:section:load", () => {
    syncToggles(getActiveTheme());
  });

  colorSchemeQuery?.addEventListener?.("change", (event) => {
    if (!readSavedTheme()) changeTheme(event.matches ? "dark" : "light");
  });

  document.addEventListener("naxos:motion-change", (event) => {
    if (!event.detail.reduced) return;
    themeTransition?.skipTransition();
    window.clearTimeout(backgroundTransitionTimer);
    root.classList.remove("naxos-theme-background-transition");
  });

  const themeColor = document.head.querySelector('meta[name="theme-color"]');
  if (themeColor) {
    new MutationObserver(() => syncThemeColor()).observe(themeColor, {
      attributes: true,
      attributeFilter: ["content"],
    });
  }

  applyTheme(getActiveTheme());
  window.requestAnimationFrame(() => root.classList.add("naxos-theme-ready"));
})();
